import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Phone, Check, AlertCircle, Loader2, RefreshCw, PhoneCall,
  Save, Volume2, PhoneForwarded, Shield, Calendar, ClipboardList,
  FileText, DollarSign, Plus, Trash2, GripVertical, RotateCcw, X,
  CreditCard, ExternalLink, Clock, Upload, CheckCircle, XCircle
} from '../../icons/wp-icons';
import { voiceAssistantApi } from '../../api/voiceAssistantApi';
import {
  getVoiceAssistantSubscription,
  redirectToVoiceAssistantCheckout
} from '../../api/stripeApi';
import toast from 'react-hot-toast';
import { successToast } from '../../utils/toastUtils';

// Dokumenten-Upload Komponente
function DocumentUploadCard({ title, description, documentType, status, onUpload, uploading, readOnly }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dateigröße prüfen (max 5MB - Twilio Limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`Die Datei ist zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximale Größe: 5 MB.\n\nBitte komprimiere die Datei oder verwende ein kleineres Bild/PDF.`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      onUpload(documentType, file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusBadge = () => {
    if (!status?.sid) return null;

    if (status.status === 'twilio-approved') {
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Genehmigt
        </span>
      );
    }
    if (status.status === 'twilio-rejected') {
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Abgelehnt
        </span>
      );
    }
    if (status.status === 'pending-review') {
      return (
        <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
          <Clock className="w-3 h-3" />
          In Prüfung
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
        <CheckCircle className="w-3 h-3" />
        Hochgeladen
      </span>
    );
  };

  return (
    <div className={`p-3 rounded-lg border ${
      status?.status === 'twilio-approved'
        ? 'bg-green-50 border-green-200'
        : status?.status === 'twilio-rejected'
        ? 'bg-red-50 border-red-200'
        : status?.status === 'pending-review'
        ? 'bg-yellow-50 border-yellow-200'
        : status?.sid
        ? 'bg-blue-50 border-blue-200'
        : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm">{title}</h4>
            {getStatusBadge()}
          </div>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
          {status?.failureReason && (
            <p className="text-xs text-red-600 mt-2 p-2 bg-red-100 rounded">
              Grund: {status.failureReason}
            </p>
          )}
        </div>

        {!readOnly && (
          <div className="shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                status?.sid && status.status !== 'twilio-rejected'
                  ? 'border border-gray-300 text-gray-600 hover:bg-gray-100'
                  : 'bg-[#378ab2] text-white hover:bg-[#2d7a9f]'
              } disabled:opacity-50`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Lädt...
                </>
              ) : status?.sid ? (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Ersetzen
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Hochladen
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Standard-Pflichtfelder
const DEFAULT_REQUIRED_FIELDS = {
  name: { label: 'Name', pflicht: true },
  phone: { label: 'Telefonnummer', pflicht: true },
  email: { label: 'E-Mail-Adresse', pflicht: false },
  address: { label: 'Adresse', pflicht: false },
  concern: { label: 'Anliegen/Problem', pflicht: true },
  preferredDate: { label: 'Wunschtermin', pflicht: false }
};

// ⚠️ KIKI-LEVEL UI-INVERSION (siehe memory/feedback_kiki_level_ui_inversion.md)
// `level` = Backend-Integer (1=AUTOMATISCH, 2=BENACHRICHTIGUNG, 3=NUR_DATEN — UNVERÄNDERT).
// `displayLevel` = Nummer die der User sieht (wächst mit Autonomiegrad).
// Reihenfolge im Array = Anzeigereihenfolge in der UI.
const AUTONOMY_LEVELS = [
  {
    level: 3,
    displayLevel: 1,
    label: 'Nur Anfragen',
    description: 'Nimmt nur Daten auf, Sie machen alles manuell',
    icon: FileText,
    color: 'gray'
  },
  {
    level: 2,
    displayLevel: 2,
    label: 'Mit Bestätigung',
    description: 'Terminvorschläge werden erstellt, Sie bestätigen/ablehnen',
    icon: ClipboardList,
    color: 'blue'
  },
  {
    level: 1,
    displayLevel: 3,
    label: 'Voll automatisch',
    description: 'Termine werden direkt im Kalender eingetragen',
    icon: Calendar,
    color: 'green'
  }
];

function TelefonAgentStep({ data = {}, onDataChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Subscription Status
  const [subscription, setSubscription] = useState({
    hasSubscription: false,
    subscriptionStatus: 'NONE',
    price: 39
  });

  // Settings State
  const [settings, setSettings] = useState({
    greetingText: '',
    forwardingNumber: '',
    autonomyLevel: 2,
    requiredFields: { ...DEFAULT_REQUIRED_FIELDS },
    priceInfoEnabled: false
  });

  // Neue Pflichtfelder
  const [newFeldKey, setNewFeldKey] = useState('');
  const [newFeldLabel, setNewFeldLabel] = useState('');

  // Regulatory Documents State
  const [regulatoryStatus, setRegulatoryStatus] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [submittingBundle, setSubmittingBundle] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const loadSubscription = async () => {
    try {
      const data = await getVoiceAssistantSubscription();
      setSubscription(data);
    } catch (err) {
      console.error('Subscription load error:', err);
    }
  };

  const loadStatus = async () => {
    try {
      const result = await voiceAssistantApi.getStatus();
      setStatus(result);
    } catch (err) {
      console.error('Fehler beim Laden des Voice Assistant Status:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await voiceAssistantApi.getSettings();

      // Konvertiere requiredFields
      let requiredFieldsObj = { ...DEFAULT_REQUIRED_FIELDS };
      if (data.requiredFields) {
        if (Array.isArray(data.requiredFields)) {
          Object.keys(requiredFieldsObj).forEach(key => {
            requiredFieldsObj[key].pflicht = data.requiredFields.includes(key);
          });
        } else if (typeof data.requiredFields === 'object') {
          requiredFieldsObj = data.requiredFields;
        }
      }

      setSettings({
        greetingText: data.greetingText || '',
        forwardingNumber: data.forwardingNumber || '',
        autonomyLevel: data.autonomyLevel ?? 2,
        requiredFields: requiredFieldsObj,
        priceInfoEnabled: data.priceInfoEnabled || false
      });
    } catch (err) {
      console.error('Fehler beim Laden der Settings:', err);
    }
  };

  const loadRegulatoryStatus = async (showFeedback = false) => {
    if (showFeedback) setCheckingStatus(true);
    try {
      const data = await voiceAssistantApi.getRegulatoryStatus(showFeedback);
      const oldStatus = regulatoryStatus?.status;
      setRegulatoryStatus(data);

      if (showFeedback) {
        if (data.status === 'twilio-approved' && oldStatus !== 'twilio-approved') {
          toast.success('Ihr Antrag wurde genehmigt! Sie können jetzt die Telefonnummer einrichten.');
        } else if (data.status === 'twilio-rejected' && oldStatus !== 'twilio-rejected') {
          toast.error('Ihr Antrag wurde leider abgelehnt. Bitte prüfen Sie die Dokumente.');
        } else if (data.status === 'pending-review') {
          toast('Antrag wird noch geprüft. Bitte haben Sie etwas Geduld.', { icon: '⏳' });
        } else {
          toast.success('Status aktualisiert');
        }
      }
    } catch (err) {
      console.error('Regulatory status load error:', err);
      if (showFeedback) {
        toast.error('Status konnte nicht geladen werden');
      }
    } finally {
      if (showFeedback) setCheckingStatus(false);
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    if (!file) return;

    setUploadingDoc(documentType);
    setError('');

    try {
      await voiceAssistantApi.uploadRegulatoryDocument(documentType, file);
      toast.success('Dokument erfolgreich hochgeladen');
      await loadRegulatoryStatus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Fehler beim Hochladen: ${errorMsg}`);
      toast.error(`Upload fehlgeschlagen: ${errorMsg}`);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmitBundle = async () => {
    setSubmittingBundle(true);
    setError('');

    try {
      const result = await voiceAssistantApi.submitRegulatoryBundle();
      toast.success(result.message || 'Antrag erfolgreich eingereicht');
      await loadRegulatoryStatus();
      await loadStatus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      toast.error(`Einreichung fehlgeschlagen: ${errorMsg}`);
    } finally {
      setSubmittingBundle(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadSubscription();
      await loadStatus();
      await loadSettings();
      await loadRegulatoryStatus();
    };
    init();
    // Immer valid, da optional
    onDataChange({}, true);
  }, []);

  // Handle URL params from Stripe redirect
  useEffect(() => {
    const subscriptionParam = searchParams.get('subscription');
    if (subscriptionParam === 'success') {
      toast.success('Telefonassistent-Abo erfolgreich aktiviert!');
      loadSubscription();
      loadStatus();
      // URL-Parameter entfernen
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('subscription');
      setSearchParams(newParams);
    } else if (subscriptionParam === 'canceled') {
      toast.error('Abo-Vorgang abgebrochen');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('subscription');
      setSearchParams(newParams);
    }
  }, [searchParams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSubscription();
    await loadStatus();
    await loadSettings();
  };

  const handleSubscribe = async () => {
    setSubscriptionLoading(true);
    try {
      await redirectToVoiceAssistantCheckout();
    } catch (err) {
      toast.error('Fehler beim Starten des Checkouts: ' + (err.response?.data?.error || err.message));
      setSubscriptionLoading(false);
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    setError('');
    try {
      const result = await voiceAssistantApi.provision();
      console.log('Voice Assistant eingerichtet:', result);

      // Prüfen ob Antrag noch auf Genehmigung wartet
      if (result.status === 'pending-review') {
        successToast('Ihr Antrag für eine deutsche Telefonnummer wurde eingereicht. Die Prüfung dauert 1-3 Werktage.', {
          duration: 8000
        });
        await loadStatus();
        return;
      }

      await loadStatus();
      await loadSettings();
      toast.success('Telefonnummer erfolgreich eingerichtet!');
    } catch (err) {
      console.error('Fehler beim Einrichten:', err);
      // Prüfen ob Abo erforderlich ist
      if (err.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
        setError('');
        // Direkt zum Checkout weiterleiten
        handleSubscribe();
        return;
      }
      setError(err.response?.data?.error || err.message || 'Fehler beim Einrichten');
    } finally {
      setProvisioning(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      await voiceAssistantApi.updateSettings({
        ...settings,
        enabled: true,
        catalogToolEnabled: settings.priceInfoEnabled
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Pflichtfelder Handler
  const handleTogglePflichtfeld = (key) => {
    setSettings(prev => ({
      ...prev,
      requiredFields: {
        ...prev.requiredFields,
        [key]: {
          ...prev.requiredFields[key],
          pflicht: !prev.requiredFields[key].pflicht
        }
      }
    }));
  };

  const handleUpdateFeldLabel = (key, newLabel) => {
    setSettings(prev => ({
      ...prev,
      requiredFields: {
        ...prev.requiredFields,
        [key]: {
          ...prev.requiredFields[key],
          label: newLabel
        }
      }
    }));
  };

  const handleAddPflichtfeld = () => {
    if (!newFeldKey || !newFeldLabel) return;
    const key = newFeldKey.toLowerCase().replace(/[^a-z0-9äöüß]/g, '_');
    if (settings.requiredFields[key]) {
      setError('Ein Feld mit diesem Schlüssel existiert bereits');
      return;
    }
    setSettings(prev => ({
      ...prev,
      requiredFields: {
        ...prev.requiredFields,
        [key]: {
          label: newFeldLabel,
          pflicht: true
        }
      }
    }));
    setNewFeldKey('');
    setNewFeldLabel('');
  };

  const handleRemovePflichtfeld = (key) => {
    setSettings(prev => {
      const newFields = { ...prev.requiredFields };
      delete newFields[key];
      return {
        ...prev,
        requiredFields: newFields
      };
    });
  };

  const handleResetPflichtfelder = () => {
    setSettings(prev => ({
      ...prev,
      requiredFields: { ...DEFAULT_REQUIRED_FIELDS }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#378ab2] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          KI Telefon-Agent einrichten
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Richten Sie eine Telefonnummer ein und konfigurieren Sie Ihren KI-Assistenten.
        </p>
      </div>

      {/* Error Anzeige */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-red-800">
            <strong>Fehler:</strong> {error}
          </div>
          <button onClick={() => setError('')}>
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {status?.hasPhoneNumber ? (
        <div className="space-y-6">
          {/* Status Box */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <Phone className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-lg font-medium text-green-900">
                    Telefon-Agent aktiv
                  </h4>
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-green-700 font-mono text-sm">
                  {status.phoneNumber}
                </p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Einstellungen */}
          <div className="border border-gray-200 rounded-xl p-6 space-y-6">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#378ab2]" />
              Einstellungen
            </h4>

            {/* Autonomie-Stufe */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-3">
                <Shield className="w-4 h-4 text-[#378ab2]" />
                Autonomie-Stufe
              </label>
              <div className="space-y-2">
                {AUTONOMY_LEVELS.map((level) => {
                  const Icon = level.icon;
                  const isSelected = settings.autonomyLevel === level.level;
                  return (
                    <label
                      key={level.level}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        isSelected
                          ? 'border-[#378ab2] bg-[#378ab2]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="autonomyLevel"
                        checked={isSelected}
                        onChange={() => setSettings(prev => ({ ...prev, autonomyLevel: level.level }))}
                        className="mt-1 text-[#378ab2] focus:ring-[#378ab2]"
                      />
                      <div className={`p-2 rounded-lg ${
                        level.color === 'green' ? 'bg-green-100' :
                        level.color === 'blue' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          level.color === 'green' ? 'text-green-600' :
                          level.color === 'blue' ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{level.label}</div>
                        <div className="text-xs text-gray-500">{level.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Pflichtfelder */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <ClipboardList className="w-4 h-4 text-[#378ab2]" />
                    Pflichtfelder für Terminvorschläge
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Diese Informationen soll der Assistent vom Anrufer erfragen.
                  </p>
                </div>
                <button
                  onClick={handleResetPflichtfelder}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 transition"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              </div>

              {/* Pflichtfelder Liste */}
              <div className="space-y-2">
                {Object.entries(settings.requiredFields || {}).map(([key, config]) => (
                  <div
                    key={key}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      value={config.label}
                      onChange={(e) => handleUpdateFeldLabel(key, e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#378ab2]"
                    />
                    <span className="text-xs text-gray-400 font-mono px-1.5 py-0.5 bg-gray-100 rounded">
                      {key}
                    </span>
                    <label className="flex items-center gap-1 shrink-0 cursor-pointer">
                      <span className="text-xs text-gray-500">Pflicht</span>
                      <input
                        type="checkbox"
                        checked={config.pflicht}
                        onChange={() => handleTogglePflichtfeld(key)}
                        className="w-4 h-4 text-[#378ab2] rounded focus:ring-[#378ab2]"
                      />
                    </label>
                    <button
                      onClick={() => handleRemovePflichtfeld(key)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded transition shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Neues Feld hinzufügen */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-blue-600" />
                  Eigenes Feld hinzufügen
                </h5>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newFeldKey}
                    onChange={(e) => setNewFeldKey(e.target.value)}
                    placeholder="Schlüssel (z.B. haustyp)"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#378ab2]"
                  />
                  <input
                    type="text"
                    value={newFeldLabel}
                    onChange={(e) => setNewFeldLabel(e.target.value)}
                    placeholder="Anzeigename (z.B. Art des Gebäudes)"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#378ab2]"
                  />
                  <button
                    onClick={handleAddPflichtfeld}
                    disabled={!newFeldKey || !newFeldLabel}
                    className="px-4 py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2d7a9f] transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Preisauskunft */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium">
                      Preisauskunft
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Der Assistent nennt unverbindliche Preisrahmen aus Ihrem Katalog.
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.priceInfoEnabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, priceInfoEnabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#378ab2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#378ab2]"></div>
                </label>
              </div>
              {settings.priceInfoEnabled && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    Der Assistent sagt immer dazu: "Das ist ein unverbindlicher Richtwert."
                  </p>
                </div>
              )}
            </div>

            {/* Begrüßungstext */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <Volume2 className="w-4 h-4 text-[#378ab2]" />
                Begrüßungstext
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Der erste Satz, den der Assistent bei einem Anruf sagt.
              </p>
              <textarea
                value={settings.greetingText}
                onChange={(e) => setSettings(prev => ({ ...prev, greetingText: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="Guten Tag, Sie sprechen mit dem automatischen Assistenten von [Firmenname]. Wie kann ich Ihnen helfen?"
              />
            </div>

            {/* Weiterleitungsnummer */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2">
                <PhoneForwarded className="w-4 h-4 text-[#378ab2]" />
                Weiterleitungsnummer
              </label>
              <p className="text-xs text-gray-500 mb-2">
                An diese Nummer wird weitergeleitet, wenn der Anrufer mit einem Menschen sprechen möchte.
              </p>
              <input
                type="tel"
                value={settings.forwardingNumber}
                onChange={(e) => setSettings(prev => ({ ...prev, forwardingNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="+49 123 456789"
              />
              {settings.forwardingNumber && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Weiterleitung aktiv
                </p>
              )}
            </div>

            {/* Speichern Button */}
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#378ab2] text-white rounded-lg font-medium hover:bg-[#2d7a9f] transition disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Speichern...
                </>
              ) : saved ? (
                <>
                  <Check className="w-5 h-5" />
                  Gespeichert!
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Einstellungen speichern
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Abo-Status Banner - Aktives Abo */}
          {subscription.hasSubscription && subscription.subscriptionStatus === 'ACTIVE' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Telefonassistent-Abo aktiv</p>
                  <p className="text-sm text-green-700">
                    {subscription.price}€ / Monat
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Einrichtungs-Card */}
          <div className="border border-gray-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <PhoneCall className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  KI-Telefonassistent
                </h4>
                <p className="text-gray-600 text-sm mb-4">
                  Mit einem Klick erhalten Sie eine eigene Telefonnummer für Ihren KI-Assistenten.
                  Anrufer werden automatisch begrüßt und ihre Anliegen werden aufgenommen.
                </p>

                {/* Features */}
                <ul className="text-sm text-gray-600 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Eigene Telefonnummer
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    24/7 Erreichbarkeit
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Automatische Terminbuchung
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Preisauskunft aus Katalog
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Weiterleitung an Mitarbeiter auf Wunsch
                  </li>
                </ul>

                {/* Abo erforderlich - Wenn kein Abo vorhanden */}
                {!subscription.hasSubscription ? (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Kostenpflichtiges Addon</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Der KI-Telefonassistent ist ein kostenpflichtiges Addon zu Ihrem bestehenden Plan.
                    </p>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-[#378ab2]">{subscription.price}€</span>
                      <span className="text-gray-500">/ Monat</span>
                    </div>
                    <button
                      onClick={handleSubscribe}
                      disabled={subscriptionLoading}
                      className="w-full px-4 py-2.5 bg-[#378ab2] text-white rounded-lg font-medium hover:bg-[#2d7a9f] transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {subscriptionLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Weiterleitung zu Stripe...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4" />
                          Jetzt abonnieren
                        </>
                      )}
                    </button>
                  </div>
                ) : regulatoryStatus?.status === 'twilio-approved' ? (
                  /* Dokumente genehmigt - Telefonnummer einrichten */
                  <button
                    onClick={handleProvision}
                    disabled={provisioning}
                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2"
                  >
                    {provisioning ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Telefonnummer wird eingerichtet...
                      </>
                    ) : (
                      <>
                        <Phone className="w-5 h-5" />
                        Telefonnummer einrichten
                      </>
                    )}
                  </button>
                ) : regulatoryStatus?.status === 'pending-review' ? (
                  /* Antrag wartet auf Genehmigung */
                  <div className="w-full p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-yellow-800">Antrag in Prüfung</p>
                        <p className="text-sm text-yellow-700 mt-1">
                          Ihr Antrag für eine deutsche Telefonnummer wird geprüft.
                          Dies dauert in der Regel 1-3 Werktage.
                        </p>
                        <button
                          onClick={() => loadRegulatoryStatus(true)}
                          disabled={checkingStatus}
                          className="mt-3 px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 transition flex items-center gap-2 disabled:opacity-50"
                        >
                          {checkingStatus ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Prüfe...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3 h-3" />
                              Status erneut prüfen
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Abo aktiv - Dokumente hochladen für deutsche Telefonnummer */
                  <div className="space-y-4">
                    {/* Status Banner */}
                    <div className={`p-3 rounded-lg border ${
                      regulatoryStatus?.status === 'twilio-rejected'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        {regulatoryStatus?.status === 'twilio-rejected' ? (
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        ) : (
                          <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className={`font-medium text-sm ${
                            regulatoryStatus?.status === 'twilio-rejected'
                              ? 'text-red-800'
                              : 'text-blue-800'
                          }`}>
                            {regulatoryStatus?.status === 'twilio-rejected'
                              ? 'Dokumente abgelehnt'
                              : 'Dokumente für deutsche Telefonnummer'}
                          </p>
                          <p className={`text-xs mt-1 ${
                            regulatoryStatus?.status === 'twilio-rejected'
                              ? 'text-red-700'
                              : 'text-blue-700'
                          }`}>
                            {regulatoryStatus?.status === 'twilio-rejected'
                              ? 'Bitte laden Sie korrigierte Versionen hoch.'
                              : 'Für eine deutsche Telefonnummer werden folgende Dokumente benötigt:'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Unternehmensnachweis */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <span>Unternehmensnachweis</span>
                        <span className="text-xs text-gray-500 font-normal">(eines der folgenden)</span>
                        {(regulatoryStatus?.documents?.businessIdentity?.sid || regulatoryStatus?.documents?.registration?.sid) && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <DocumentUploadCard
                          title="Handelsregisterauszug"
                          description="Für GmbH, UG, AG etc. (max 6 Monate alt)"
                          documentType="businessIdentity"
                          status={regulatoryStatus?.documents?.businessIdentity}
                          onUpload={handleDocumentUpload}
                          uploading={uploadingDoc === 'businessIdentity'}
                          readOnly={regulatoryStatus?.status === 'pending-review'}
                        />
                        <DocumentUploadCard
                          title="Gewerbeanmeldung"
                          description="Für Einzelunternehmer ohne HR-Eintrag"
                          documentType="registration"
                          status={regulatoryStatus?.documents?.registration}
                          onUpload={handleDocumentUpload}
                          uploading={uploadingDoc === 'registration'}
                          readOnly={regulatoryStatus?.status === 'pending-review'}
                        />
                      </div>
                    </div>

                    {/* Adressnachweis */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <span>Adressnachweis</span>
                        <span className="text-xs text-gray-500 font-normal">(Pflicht)</span>
                        {regulatoryStatus?.documents?.proofOfAddress?.sid && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      <DocumentUploadCard
                        title="Adressnachweis"
                        description="Rechnung eines Versorgers, Kontoauszug oder amtliches Dokument"
                        documentType="proofOfAddress"
                        status={regulatoryStatus?.documents?.proofOfAddress}
                        onUpload={handleDocumentUpload}
                        uploading={uploadingDoc === 'proofOfAddress'}
                        readOnly={regulatoryStatus?.status === 'pending-review'}
                      />
                    </div>

                    {/* Einreichen Button */}
                    <div className="flex gap-3">
                      <button
                        onClick={handleSubmitBundle}
                        disabled={submittingBundle || !(regulatoryStatus?.documents?.businessIdentity?.sid || regulatoryStatus?.documents?.registration?.sid) || !regulatoryStatus?.documents?.proofOfAddress?.sid}
                        className="flex-1 px-4 py-2.5 bg-[#378ab2] text-white rounded-lg font-medium hover:bg-[#2d7a9f] transition flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {submittingBundle ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Wird eingereicht...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Antrag einreichen
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => loadRegulatoryStatus(true)}
                        disabled={checkingStatus}
                        className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${checkingStatus ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status aktualisieren */}
          <div className="flex justify-center">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm text-[#378ab2] hover:bg-[#378ab2]/10 rounded-lg transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Wird aktualisiert...' : 'Status aktualisieren'}
            </button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Tipp:</strong> Dieser Schritt ist optional. Sie können den Telefonassistenten auch später
              in den Einstellungen einrichten.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TelefonAgentStep;
